"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import type { ServiceDetails } from "@louez/types";
import { hasServiceDetailsContent } from "@louez/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@louez/ui";
import { ChevronsUpDownIcon, ClockIcon } from "@louez/ui/icons";
import { cn } from "@louez/utils";

type ServiceDetailSummaryProps = {
  categoryLabel: string;
  customQuoteLabel: string;
  description: string | null;
  details: ServiceDetails;
  durationLabel: string;
  name: string;
  priceLabel: string | null;
};

type AccordionSection = {
  id: string;
  title: string;
  items?: string[];
  text?: string | null;
};

export const ServiceDetailSummary = ({
  categoryLabel,
  customQuoteLabel,
  description,
  details,
  durationLabel,
  name,
  priceLabel,
}: ServiceDetailSummaryProps) => {
  const t = useTranslations("storefront.services");
  const sections: AccordionSection[] = [
    {
      id: "howItWorks",
      title: t("detailsHowItWorks"),
      items: details.howItWorks,
    },
    {
      id: "includes",
      title: t("detailsIncludes"),
      items: details.includes,
    },
    {
      id: "notes",
      title: t("detailsNotes"),
      items: details.notes,
    },
    {
      id: "afterRequest",
      title: t("detailsAfterRequest"),
      text: details.afterRequest,
    },
  ].filter(
    (section) =>
      (section.items && section.items.length > 0) ||
      Boolean(section.text?.trim()),
  );
  const [openSectionId, setOpenSectionId] = useState<string | null>(
    sections[0]?.id ?? null,
  );

  return (
    <section className="space-y-5">
      <div>
        <p className="text-primary text-sm font-medium">{categoryLabel}</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
          {name}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <p className="text-2xl font-bold">{priceLabel ?? customQuoteLabel}</p>
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <ClockIcon className="size-4" />
            {durationLabel}
          </p>
        </div>
      </div>

      {description && (
        <p className="text-muted-foreground text-sm leading-6 sm:text-[0.95rem] sm:leading-7">
          {description}
        </p>
      )}

      {hasServiceDetailsContent(details) && (
        <div className="divide-border border-border divide-y overflow-hidden rounded-xl border">
          {sections.map((section) => {
            const isOpen = openSectionId === section.id;
            return (
              <Collapsible
                key={section.id}
                open={isOpen}
                onOpenChange={(open) =>
                  setOpenSectionId(open ? section.id : null)
                }
              >
                <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium">
                  <span>{section.title}</span>
                  <ChevronsUpDownIcon
                    className={cn(
                      "text-muted-foreground size-4 shrink-0 transition-transform",
                      isOpen && "rotate-180",
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="text-muted-foreground px-4 pb-4 text-sm leading-6">
                    {section.items && section.items.length > 0 ? (
                      <ol className="list-decimal space-y-2 pl-4">
                        {section.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ol>
                    ) : (
                      <p>{section.text}</p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      )}
    </section>
  );
};
